import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Layout, Button, Space, Typography, Spin, message, Tooltip, Dropdown } from 'antd';
import {
    ArrowLeftOutlined,
    CloudSyncOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    AppstoreOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    BorderOutlined,
    EyeOutlined,
    UndoOutlined,
    RedoOutlined,
    LayoutOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { TransformWrapper, TransformComponent, useTransformContext } from 'react-zoom-pan-pinch';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useUIDesign } from './contexts/UIDesignContext';

import {
    useCanvas,
    useArtboards,
    useCreateDefaultArtboards,
    useUIComponents,
    useCreateUIComponent,
    useUpdateUIComponent,
    useDeleteUIComponent,
    useSetActiveArtboard,
    useUpdateArtboard,
    useMergedDesignKit,
    useUpdateDesignKitComponent
} from '../../api/canvasApi';

import Artboard from './components/Artboard';
import DesignKitPanel from './components/DesignKitPanel';
import ComponentInspector from './components/ComponentInspector';
import DesignKitShowcase from './components/DesignKitShowcase';

const { Header, Content, Sider } = Layout;
const { Text, Title } = Typography;

// Artboard presets matching backend
const ARTBOARD_PRESETS = {
    mobile: { width: 375, height: 812, label: 'iPhone', icon: <MobileOutlined /> },
    tablet: { width: 768, height: 1024, label: 'iPad', icon: <TabletOutlined /> },
    desktop: { width: 1440, height: 900, label: 'Web', icon: <DesktopOutlined /> }
};

const UIDesignEditor = () => {
    const { projectId: canvasId } = useParams();
    const navigate = useNavigate();
    const canvasRef = useRef(null);
    const transformRef = useRef(null);  // Ref to TransformWrapper for programmatic control
    const hasCenteredRef = useRef(false);  // Track if we've centered on initial load

    // API Queries
    const { data: canvas, isLoading: canvasLoading } = useCanvas(canvasId);
    const { data: artboards = [], isLoading: artboardsLoading, refetch: refetchArtboards } = useArtboards(canvasId);
    const { data: uiComponents = [], isLoading: componentsLoading } = useUIComponents(canvasId);
    const { data: designKit, isLoading: kitLoading, refetch: refetchDesignKit } = useMergedDesignKit(canvasId);

    // API Mutations
    const createDefaultArtboardsMutation = useCreateDefaultArtboards(canvasId);
    const createUIComponentMutation = useCreateUIComponent(canvasId);
    const updateUIComponentMutation = useUpdateUIComponent(canvasId);
    const deleteUIComponentMutation = useDeleteUIComponent(canvasId);
    const setActiveArtboardMutation = useSetActiveArtboard(canvasId);
    const updateArtboardMutation = useUpdateArtboard(canvasId);
    const updateDesignKitComponentMutation = useUpdateDesignKitComponent();

    // Shared state from context (for header toolbar)
    const { viewMode, setViewMode, showGrid, setShowGrid, syncScroll, setSyncScroll } = useUIDesign();

    // Local State
    const [isSaving, setIsSaving] = useState(false);
    const [selectedComponentIds, setSelectedComponentIds] = useState(new Set());
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [localActiveArtboardId, setLocalActiveArtboardId] = useState(null);
    const [artboardPositions, setArtboardPositions] = useState({}); // { artboardId: { x, y } }
    const [sidebarWidth, setSidebarWidth] = useState(360); // Resizable sidebar width
    const [isResizing, setIsResizing] = useState(false);
    const [showDesignKit, setShowDesignKit] = useState(true); // Toggle for design kit sidebar
    const clipboardRef = useRef(null); // For copy/paste of components

    // Create default artboards if none exist
    useEffect(() => {
        if (!artboardsLoading && artboards.length === 0 && canvasId) {
            createDefaultArtboardsMutation.mutateAsync().then(() => {
                refetchArtboards();
            });
        }
    }, [artboardsLoading, artboards.length, canvasId]);

    // Initialize artboard positions from database (only on first load or for new artboards)
    useEffect(() => {
        if (artboards.length > 0) {
            setArtboardPositions(prev => {
                const newPositions = { ...prev };
                let hasChanges = false;

                artboards.forEach(ab => {
                    // Only set position if we don't already have it locally
                    // This prevents overwriting user drags when artboards refetch
                    if (!newPositions[ab.id] && (ab.position_x !== undefined || ab.position_y !== undefined)) {
                        newPositions[ab.id] = { x: ab.position_x || 0, y: ab.position_y || 0 };
                        hasChanges = true;
                    }
                });

                return hasChanges ? newPositions : prev;
            });
        }
    }, [artboards]);

    // Get active artboard (use local state for immediate response, fall back to server state)
    const activeArtboard = useMemo(() => {
        if (localActiveArtboardId) {
            const localActive = artboards.find(a => a.id === localActiveArtboardId);
            if (localActive) return localActive;
        }
        return artboards.find(a => a.is_active) || artboards[0];
    }, [artboards, localActiveArtboardId]);

    // Get selected components
    const selectedComponents = useMemo(() => {
        return uiComponents.filter(c => selectedComponentIds.has(c.id));
    }, [uiComponents, selectedComponentIds]);

    // Get single selected component (for inspector)
    const selectedComponent = selectedComponents.length === 1 ? selectedComponents[0] : null;

    // Group components by artboard
    const componentsByArtboard = useMemo(() => {
        const grouped = {};
        artboards.forEach(ab => {
            grouped[ab.id] = uiComponents.filter(c => c.artboard_id === ab.id);
        });
        return grouped;
    }, [artboards, uiComponents]);

    // Handle artboard click to set active (optimistic update)
    const handleArtboardClick = useCallback((artboardId) => {
        // Immediate local update for responsiveness
        setLocalActiveArtboardId(artboardId);
        // Background API call to persist
        setActiveArtboardMutation.mutate(artboardId);
    }, [setActiveArtboardMutation]);

    // Handle component selection (with Shift for multi-select)
    const handleComponentSelect = useCallback((componentId, event) => {
        if (!componentId) {
            setSelectedComponentIds(new Set());
            return;
        }
        if (event?.shiftKey) {
            // Toggle selection
            setSelectedComponentIds(prev => {
                const next = new Set(prev);
                if (next.has(componentId)) {
                    next.delete(componentId);
                } else {
                    next.add(componentId);
                }
                return next;
            });
        } else {
            // Single select
            setSelectedComponentIds(new Set([componentId]));
        }
    }, []);

    // Handle component drop from design kit
    const handleComponentDrop = useCallback(async (componentType, artboardId, position) => {
        setIsSaving(true);
        try {
            const kitComponent = designKit?.components?.atoms?.find(c => c.type === componentType)
                || designKit?.components?.molecules?.find(c => c.type === componentType)
                || designKit?.components?.organisms?.find(c => c.type === componentType);

            const artboard = artboards.find(a => a.id === artboardId);
            const artboardWidth = artboard?.width || 375;

            // Default height based on component type
            let defaultHeight = 40;
            if (kitComponent) {
                if (typeof kitComponent.defaultHeight === 'object') {
                    defaultHeight = kitComponent.defaultHeight[artboard?.device_type] || 40;
                } else {
                    defaultHeight = kitComponent.defaultHeight || 40;
                }
            }

            // Make component full width and centered (x = 0)
            const dimensions = {
                width: artboardWidth,
                height: defaultHeight
            };

            await createUIComponentMutation.mutateAsync({
                artboard_id: artboardId,
                component_type: componentType,
                component_kit_ref: kitComponent?.id || '',
                position_x: 0,  // Full width, so start at 0
                position_y: position.y,  // Keep the y position from drop
                width: dimensions.width,
                height: dimensions.height,
                state: 'default'
            });
            message.success(`Added ${componentType} component`);
        } catch (error) {
            console.error('Failed to add component:', error);
            message.error('Failed to add component');
        }
        setIsSaving(false);
    }, [designKit, artboards, createUIComponentMutation]);

    // Handle component move
    const handleComponentMove = useCallback(async (componentId, position) => {
        setIsSaving(true);
        try {
            await updateUIComponentMutation.mutateAsync({
                componentId,
                data: { position_x: position.x, position_y: position.y }
            });
        } catch (error) {
            console.error('Failed to move component:', error);
        }
        setIsSaving(false);
    }, [updateUIComponentMutation]);

    // Handle group move (move all selected components by delta)
    const handleGroupMove = useCallback(async (draggedComponentId, delta) => {
        // Get all selected component IDs except the one being directly dragged
        const otherSelectedIds = [...selectedComponentIds].filter(id => id !== draggedComponentId);
        if (otherSelectedIds.length === 0) return;

        setIsSaving(true);
        try {
            // Move all other selected components by the same delta
            await Promise.all(otherSelectedIds.map(async (compId) => {
                const comp = uiComponents.find(c => c.id === compId);
                if (comp) {
                    await updateUIComponentMutation.mutateAsync({
                        componentId: compId,
                        data: {
                            position_x: (comp.position_x || 0) + delta.x,
                            position_y: (comp.position_y || 0) + delta.y
                        }
                    });
                }
            }));
        } catch (error) {
            console.error('Failed to move group:', error);
        }
        setIsSaving(false);
    }, [selectedComponentIds, uiComponents, updateUIComponentMutation]);

    // Handle component resize
    const handleComponentResize = useCallback(async (componentId, dimensions) => {
        setIsSaving(true);
        try {
            await updateUIComponentMutation.mutateAsync({
                componentId,
                data: { width: dimensions.width, height: dimensions.height }
            });
        } catch (error) {
            console.error('Failed to resize component:', error);
        }
        setIsSaving(false);
    }, [updateUIComponentMutation]);

    // Handle component delete
    const handleComponentDelete = useCallback(async (componentId) => {
        setIsSaving(true);
        try {
            await deleteUIComponentMutation.mutateAsync(componentId);
            setSelectedComponentIds(prev => {
                const next = new Set(prev);
                next.delete(componentId);
                return next;
            });
            message.success('Component deleted');
        } catch (error) {
            console.error('Failed to delete component:', error);
            message.error('Failed to delete component');
        }
        setIsSaving(false);
    }, [deleteUIComponentMutation]);

    // Handle component state change
    const handleComponentStateChange = useCallback(async (componentId, state) => {
        setIsSaving(true);
        try {
            await updateUIComponentMutation.mutateAsync({
                componentId,
                data: { state }
            });
        } catch (error) {
            console.error('Failed to update component state:', error);
        }
        setIsSaving(false);
    }, [updateUIComponentMutation]);

    // Handle artboard position change (drag by title)
    const handleArtboardMove = useCallback((artboardId, position) => {
        // Update local state immediately for smooth visual feedback
        setArtboardPositions(prev => ({
            ...prev,
            [artboardId]: position
        }));
        // Persist to database
        updateArtboardMutation.mutate({
            artboardId,
            data: { position_x: position.x, position_y: position.y }
        });
    }, [updateArtboardMutation]);

    // Handle design kit component update
    const handleComponentUpdate = useCallback(async (componentId, updates) => {
        setIsSaving(true);
        try {
            await updateDesignKitComponentMutation.mutateAsync({
                componentId,
                data: updates
            });
            message.success('Component updated successfully');
        } catch (error) {
            console.error('Failed to update component:', error);
            message.error('Failed to update component');
        }
        setIsSaving(false);
    }, [updateDesignKitComponentMutation]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore shortcuts when typing in input fields
            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.isContentEditable ||
                activeElement.closest('.ant-input') ||
                activeElement.closest('.ant-select')
            );

            if (isInputFocused) {
                return; // Don't handle shortcuts when typing
            }

            // Delete selected components
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedComponentIds.size > 0) {
                e.preventDefault();
                selectedComponentIds.forEach(id => handleComponentDelete(id));
            }
            // Switch artboards with 1, 2, 3
            if (e.key === '1' && artboards[0]) {
                setLocalActiveArtboardId(artboards[0].id);
                setActiveArtboardMutation.mutate(artboards[0].id);
            }
            if (e.key === '2' && artboards[1]) {
                setLocalActiveArtboardId(artboards[1].id);
                setActiveArtboardMutation.mutate(artboards[1].id);
            }
            if (e.key === '3' && artboards[2]) {
                setLocalActiveArtboardId(artboards[2].id);
                setActiveArtboardMutation.mutate(artboards[2].id);
            }
            // Toggle grid
            if (e.key === 'g' && !e.ctrlKey && !e.metaKey) {
                setShowGrid(prev => !prev);
            }
            // Undo/Redo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                if (e.shiftKey) {
                    // Redo
                    console.log('Redo');
                } else {
                    // Undo
                    console.log('Undo');
                }
            }

            // Copy (Cmd/Ctrl + C) - supports multiple components
            if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedComponentIds.size > 0) {
                e.preventDefault();
                const componentsToCopy = uiComponents.filter(c => selectedComponentIds.has(c.id));
                if (componentsToCopy.length > 0) {
                    clipboardRef.current = componentsToCopy.map(comp => ({
                        component_type: comp.component_type,
                        state: comp.state,
                        content_overrides: comp.content_overrides,
                        width: comp.width,
                        height: comp.height,
                        position_x: comp.position_x,
                        position_y: comp.position_y,
                        artboard_id: comp.artboard_id
                    }));
                    message.success(`Copied ${componentsToCopy.length} component${componentsToCopy.length > 1 ? 's' : ''}`);
                }
            }

            // Paste (Cmd/Ctrl + V) - supports multiple components
            if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboardRef.current) {
                e.preventDefault();
                const clipboardData = Array.isArray(clipboardRef.current) ? clipboardRef.current : [clipboardRef.current];
                const targetArtboard = localActiveArtboardId || clipboardData[0]?.artboard_id;
                if (targetArtboard) {
                    clipboardData.forEach((pasteData, index) => {
                        createUIComponentMutation.mutate({
                            artboard_id: targetArtboard,
                            component_type: pasteData.component_type,
                            position_x: (pasteData.position_x || 0) + 20,
                            position_y: (pasteData.position_y || 0) + 20,
                            width: pasteData.width,
                            height: pasteData.height,
                            state: pasteData.state,
                            content_overrides: pasteData.content_overrides
                        });
                    });
                    message.success(`Pasted ${clipboardData.length} component${clipboardData.length > 1 ? 's' : ''}`);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedComponentIds, artboards, handleComponentDelete, setActiveArtboardMutation, uiComponents, localActiveArtboardId, createUIComponentMutation]);

    // View mode filter
    const visibleArtboards = useMemo(() => {
        if (viewMode === 'all') return artboards;
        return artboards.filter(a => a.device_type === viewMode);
    }, [artboards, viewMode]);

    const goBack = () => navigate('/');

    if (canvasLoading || artboardsLoading) {
        return (
            <Layout style={{ height: '100vh', background: '#0d1117' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout style={{ height: 'calc(100vh - 64px)', background: '#0d1117' }}>
            <Layout style={{ display: 'flex', flexDirection: 'row' }}>
                {/* Main Canvas Area */}
                <Content style={{
                    background: '#0d1117',
                    overflow: 'hidden',
                    position: 'relative',
                    flex: 1
                }}>
                    <TransformWrapper
                        initialScale={0.5}
                        minScale={0.1}
                        maxScale={3}
                        limitToBounds={false}
                        velocityAnimation={{
                            sensitivity: 1,
                            animationTime: 100,
                            animationType: 'easeOut',
                            equalToMove: true
                        }}
                        wheel={{
                            step: 0.15,
                            smoothStep: 0.003,
                            wheelDisabled: false
                        }}
                        pinch={{
                            step: 10,
                            disabled: false
                        }}
                        panning={{
                            velocityDisabled: false
                        }}
                        doubleClick={{
                            disabled: true
                        }}
                        ref={transformRef}
                        onInit={(ref) => {
                            // Center on first artboard after initial render
                            if (!hasCenteredRef.current && artboards.length > 0 && ref) {
                                hasCenteredRef.current = true;
                                setTimeout(() => {
                                    // Target first artboard 
                                    // Artboard header is ~50px, canvas has 200px padding
                                    const targetArtboard = artboards[0];
                                    const scale = 0.5;
                                    // Artboard content starts after padding (200) + header (~50)
                                    const artboardCenterX = 200 + (targetArtboard.width / 2);
                                    const artboardCenterY = 200 + 50 + (targetArtboard.height / 2); // +50 for header

                                    const wrapperEl = document.querySelector('.react-transform-wrapper');
                                    if (!wrapperEl || !ref.setTransform) return;

                                    // Calculate transform - invert because transform moves content, not viewport
                                    const x = (wrapperEl.clientWidth / 2) - (artboardCenterX * scale);
                                    const y = (wrapperEl.clientHeight / 2) - (artboardCenterY * scale);
                                    ref.setTransform(x, y, scale, 0);
                                }, 300);
                            }
                        }}
                    >
                        {({ zoomIn, zoomOut, resetTransform }) => (
                            <>
                                {/* Zoom Controls - Bottom Right */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: 16,
                                    right: 16,
                                    zIndex: 100,
                                    display: 'flex',
                                    gap: 8
                                }}>
                                    <Button icon={<ZoomOutOutlined />} onClick={() => zoomOut()} size="small" />
                                    <Button onClick={() => resetTransform()} size="small">Reset</Button>
                                    <Button icon={<ZoomInOutlined />} onClick={() => zoomIn()} size="small" />
                                </div>

                                {/* Design Kit Toggle - Top Right */}
                                <Button
                                    icon={<LayoutOutlined />}
                                    type={showDesignKit ? 'primary' : 'default'}
                                    onClick={() => setShowDesignKit(!showDesignKit)}
                                    style={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        zIndex: 100
                                    }}
                                />

                                <TransformComponent
                                    wrapperStyle={{ width: '100%', height: '100%' }}
                                    contentStyle={{ width: 'auto', height: 'auto' }}
                                >
                                    <div
                                        ref={canvasRef}
                                        onMouseDown={(e) => {
                                            // Click directly on canvas div OR any area not inside an artboard/component
                                            const clickedInsideArtboard = e.target.closest('[data-artboard-wrapper]') ||
                                                e.target.closest('.artboard-canvas');
                                            if (!clickedInsideArtboard) {
                                                setSelectedComponentIds(new Set());
                                                setLocalActiveArtboardId(null);
                                            }
                                        }}
                                        style={{
                                            display: 'flex',
                                            gap: 100,
                                            padding: 200,
                                            minWidth: 3000,
                                            minHeight: 2000
                                        }}
                                    >
                                        {visibleArtboards.map((artboard, index) => {
                                            const offset = artboardPositions[artboard.id];
                                            const style = offset ? {
                                                transform: `translate(${offset.x}px, ${offset.y}px)`,
                                                zIndex: 10
                                            } : {};

                                            return (
                                                <div key={artboard.id} data-artboard-wrapper style={style}>
                                                    <Artboard
                                                        artboard={artboard}
                                                        components={componentsByArtboard[artboard.id] || []}
                                                        isActive={artboard.id === activeArtboard?.id}
                                                        showGrid={showGrid}
                                                        selectedComponentIds={selectedComponentIds}
                                                        onArtboardClick={() => handleArtboardClick(artboard.id)}
                                                        onComponentSelect={handleComponentSelect}
                                                        onComponentMove={handleComponentMove}
                                                        onGroupMove={handleGroupMove}
                                                        onComponentResize={handleComponentResize}
                                                        onComponentDrop={(type, pos) => handleComponentDrop(type, artboard.id, pos)}
                                                        onArtboardMove={(delta) => handleArtboardMove(artboard.id, delta)}
                                                        currentOffset={offset || { x: 0, y: 0 }}
                                                        designKit={designKit}
                                                    />
                                                </div>
                                            );
                                        })}

                                        {/* Design Kit Showcase */}
                                        {viewMode === 'all' && (
                                            <DesignKitShowcase
                                                designKit={designKit}
                                                artboardWidth={800}
                                            />
                                        )}
                                    </div>
                                </TransformComponent>
                            </>
                        )}
                    </TransformWrapper>
                </Content>

                {/* Right Sidebar - Design Kit & Inspector (Resizable) */}
                {showDesignKit && (
                    <div style={{
                        width: sidebarWidth,
                        minWidth: 280,
                        maxWidth: 600,
                        background: '#161b22',
                        borderLeft: '1px solid #30363d',
                        overflow: 'hidden',
                        display: 'flex',
                        position: 'relative',
                        flexShrink: 0
                    }}>
                        {/* Resize Handle */}
                        <div
                            style={{
                                width: 6,
                                cursor: 'ew-resize',
                                background: isResizing ? '#58a6ff' : 'transparent',
                                transition: isResizing ? 'none' : 'background 0.2s',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                zIndex: 10
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizing(true);
                                const startX = e.clientX;
                                const startWidth = sidebarWidth;

                                const handleMouseMove = (moveEvent) => {
                                    const delta = startX - moveEvent.clientX;
                                    const newWidth = Math.min(600, Math.max(280, startWidth + delta));
                                    setSidebarWidth(newWidth);
                                };

                                const handleMouseUp = () => {
                                    setIsResizing(false);
                                    document.removeEventListener('mousemove', handleMouseMove);
                                    document.removeEventListener('mouseup', handleMouseUp);
                                };

                                document.addEventListener('mousemove', handleMouseMove);
                                document.addEventListener('mouseup', handleMouseUp);
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(88, 166, 255, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                if (!isResizing) {
                                    e.currentTarget.style.background = 'transparent';
                                }
                            }}
                        />

                        {/* Sidebar Content */}
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            {selectedComponent ? (
                                <ComponentInspector
                                    component={selectedComponent}
                                    artboards={artboards}
                                    onStateChange={handleComponentStateChange}
                                    onDelete={handleComponentDelete}
                                    onUpdate={(componentId, data) => updateUIComponentMutation.mutate({ componentId, data })}
                                />
                            ) : (
                                <DesignKitPanel
                                    designKit={designKit}
                                    isLoading={kitLoading}
                                    activeDeviceType={activeArtboard?.device_type || 'mobile'}
                                    panelWidth={sidebarWidth}
                                    onComponentUpdate={handleComponentUpdate}
                                    canvasId={canvasId}
                                    onRefresh={refetchDesignKit}
                                />
                            )}
                        </div>
                    </div>
                )}
            </Layout>
        </Layout>
    );
};

// Wrap with DndProvider for drag-and-drop support
const UIDesignEditorWithDnd = () => (
    <DndProvider backend={HTML5Backend}>
        <UIDesignEditor />
    </DndProvider>
);

export default UIDesignEditorWithDnd;
